import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Chip,
} from '@mui/material';
import { Heart, Trash2 } from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  CartesianGrid,
} from 'recharts';
import { BloodPressureEntry, DatabaseSchema } from '../../types';

interface Props {
  database: DatabaseSchema | null;
  onAddBloodPressure: (entry: BloodPressureEntry) => Promise<void>;
  onDeleteBloodPressure: (id: string) => Promise<void>;
}

const TIMES: BloodPressureEntry['timeOfDay'][] = ['morning', 'afternoon', 'evening'];

export const BloodPressureTracker: React.FC<Props> = ({ database, onAddBloodPressure, onDeleteBloodPressure }) => {
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [pulse, setPulse] = useState('');
  const [reading, setReading] = useState<1 | 2 | 3>(1);
  const [timeOfDay, setTimeOfDay] = useState<BloodPressureEntry['timeOfDay']>('morning');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const entries = useMemo(() => database?.bloodPressure ?? [], [database?.bloodPressure]);
  const target = database?.preferences?.bpTarget ?? { systolic: 135, diastolic: 85 };

  const last14 = useMemo(() => {
    const cutoff = subDays(new Date(), 14);
    return entries
      .filter((e) => parseISO(e.date) >= cutoff)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [entries]);

  const avg14 = useMemo(() => {
    if (!last14.length) return null;
    const sys = last14.reduce((s, e) => s + e.systolic, 0) / last14.length;
    const dia = last14.reduce((s, e) => s + e.diastolic, 0) / last14.length;
    return { systolic: Math.round(sys), diastolic: Math.round(dia) };
  }, [last14]);

  const chartData = useMemo(
    () =>
      entries
        .slice()
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((e) => ({
          date: format(parseISO(e.date), 'd MMM'),
          systolic: e.systolic,
          diastolic: e.diastolic,
        })),
    [entries]
  );

  const handleSave = async () => {
    const sys = Number(systolic);
    const dia = Number(diastolic);
    if (!sys || !dia) return;
    setSaving(true);
    try {
      await onAddBloodPressure({
        id: `bp-${Date.now()}-${reading}`,
        date: format(new Date(), 'yyyy-MM-dd'),
        systolic: sys,
        diastolic: dia,
        pulse: pulse ? Number(pulse) : undefined,
        reading,
        timeOfDay,
        notes: notes || undefined,
      });
      setSystolic('');
      setDiastolic('');
      setPulse('');
      setNotes('');
      setReading(reading === 3 ? 1 : (reading + 1) as 1 | 2 | 3);
    } finally {
      setSaving(false);
    }
  };

  const recent = [...entries].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 10);
  const exceedsTarget = avg14 && (avg14.systolic > target.systolic || avg14.diastolic > target.diastolic);

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Heart size={22} />
            <Typography variant="h6" fontWeight={700}>Blood pressure</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            GP protocol: best of 3 readings, 3×/day for 2 weeks. Target avg &lt; {target.systolic}/{target.diastolic}.
          </Typography>

          {avg14 && (
            <Box sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: exceedsTarget ? 'warning.light' : 'success.light',
              color: exceedsTarget ? 'warning.contrastText' : 'success.contrastText',
            }}>
              <Typography variant="caption">14-day average ({last14.length} readings)</Typography>
              <Typography variant="h5" fontWeight={700}>
                {avg14.systolic}/{avg14.diastolic}
              </Typography>
              {exceedsTarget && (
                <Typography variant="caption">Above target — consider a GP review.</Typography>
              )}
            </Box>
          )}

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField label="Systolic" type="number" size="small" value={systolic} onChange={(e) => setSystolic(e.target.value)} fullWidth inputProps={{ inputMode: 'numeric' }} />
            <TextField label="Diastolic" type="number" size="small" value={diastolic} onChange={(e) => setDiastolic(e.target.value)} fullWidth inputProps={{ inputMode: 'numeric' }} />
            <TextField label="Pulse" type="number" size="small" value={pulse} onChange={(e) => setPulse(e.target.value)} fullWidth inputProps={{ inputMode: 'numeric' }} />
          </Stack>

          <Typography variant="caption" color="text.secondary">Reading #</Typography>
          <ToggleButtonGroup
            value={reading}
            exclusive
            onChange={(_, v) => v && setReading(v)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton value={1}>1st</ToggleButton>
            <ToggleButton value={2}>2nd</ToggleButton>
            <ToggleButton value={3}>3rd</ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="caption" color="text.secondary">Time of day</Typography>
          <ToggleButtonGroup
            value={timeOfDay}
            exclusive
            onChange={(_, v) => v && setTimeOfDay(v)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          >
            {TIMES.map((t) => (
              <ToggleButton key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField label="Notes (optional)" size="small" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth sx={{ mb: 2 }} />

          <Button variant="contained" size="large" fullWidth onClick={handleSave} disabled={!systolic || !diastolic || saving}>
            Save reading
          </Button>
        </CardContent>
      </Card>

      {chartData.length > 1 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Trend</Typography>
            <Box sx={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <ReferenceLine y={target.systolic} strokeDasharray="4 4" stroke="#f59e0b" label={{ value: `Target sys ${target.systolic}`, fontSize: 10, position: 'insideTopRight' }} />
                  <ReferenceLine y={target.diastolic} strokeDasharray="4 4" stroke="#f59e0b" />
                  <Line type="monotone" dataKey="systolic" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="diastolic" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {recent.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Recent readings</Typography>
            {recent.map((e) => (
              <Box
                key={e.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {format(parseISO(e.date), 'd MMM')} · {e.systolic}/{e.diastolic}
                    {e.pulse ? ` · ♥ ${e.pulse}` : ''}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                    <Chip label={`#${e.reading}`} size="small" variant="outlined" />
                    <Chip label={e.timeOfDay} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  </Stack>
                </Box>
                <IconButton size="small" onClick={() => onDeleteBloodPressure(e.id)} sx={{ color: 'text.secondary' }}>
                  <Trash2 size={16} />
                </IconButton>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
